import { addDays, addMonths, type LocalDate } from '../dates';
import { totalSpent, type DateRange, type LedgerEntryLike } from '../ledger/counting';
import { normalizeNote } from './notes';
import { daysBetween, monthEnd } from './range';

export type MonthKey = string; // 'YYYY-MM'
export type Cadence = 'weekly' | 'biweekly' | 'monthly';

export interface MonthTotal {
  month: MonthKey;
  totalMinor: number;
  changeMinor: number;
  changePct: number | null;
}

export interface PeriodChange {
  currentMinor: number;
  previousMinor: number;
  changeMinor: number;
  changePct: number | null;
}

export interface RecurringSpend {
  key: string;
  label: string;
  cadence: Cadence;
  typicalAmountMinor: number;
  occurrences: number;
  lastDate: LocalDate;
  nextExpected: LocalDate;
  entryIds: string[];
}

export interface RecurringOptions {
  minOccurrences?: number; // default 3
  amountTolerance?: number; // default 0.2 (±20% of the median)
}

const WINDOWS: Record<Cadence, [number, number]> = {
  weekly: [6, 8],
  biweekly: [13, 16],
  monthly: [27, 33],
};

export function monthRange(month: MonthKey): DateRange {
  const start = `${month}-01`;
  return { start, end: monthEnd(start) };
}

/** Percent change with one decimal; null when the previous value is 0. */
export function pctChange(current: number, previous: number): number | null {
  if (previous === 0) return null;
  return Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10;
}

export function periodChange(
  entries: readonly LedgerEntryLike[],
  current: DateRange,
  previous: DateRange,
): PeriodChange {
  const currentMinor = totalSpent(entries, current);
  const previousMinor = totalSpent(entries, previous);
  return {
    currentMinor,
    previousMinor,
    changeMinor: currentMinor - previousMinor,
    changePct: pctChange(currentMinor, previousMinor),
  };
}

/** `count` calendar months ending at `endMonth`, each with its change from the month before. */
export function monthlyTotals(
  entries: readonly LedgerEntryLike[],
  endMonth: MonthKey,
  count: number,
): MonthTotal[] {
  const first = addMonths(`${endMonth}-01`, -(count - 1));
  let prev = totalSpent(entries, monthRange(addMonths(first, -1).slice(0, 7)));
  const out: MonthTotal[] = [];
  for (let i = 0; i < count; i++) {
    const month = addMonths(first, i).slice(0, 7);
    const totalMinor = totalSpent(entries, monthRange(month));
    out.push({
      month,
      totalMinor,
      changeMinor: totalMinor - prev,
      changePct: pctChange(totalMinor, prev),
    });
    prev = totalMinor;
  }
  return out;
}

function median(xs: readonly number[]): number {
  const s = [...xs].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 === 1 ? (s[mid] ?? 0) : Math.round(((s[mid - 1] ?? 0) + (s[mid] ?? 0)) / 2);
}

/**
 * Spends with the same note at a regular interval (weekly, every two weeks, monthly) and a
 * steady amount. One entry per day counts; refunds, transfers and deleted entries are ignored.
 */
export function detectRecurring(
  entries: readonly LedgerEntryLike[],
  opts: RecurringOptions = {},
): RecurringSpend[] {
  const min = opts.minOccurrences ?? 3;
  const tolerance = opts.amountTolerance ?? 0.2;
  const groups = new Map<string, LedgerEntryLike[]>();
  for (const e of entries) {
    if (e.deletedAt !== null || e.kind !== 'expense') continue;
    const key = normalizeNote(e.note);
    if (key === '') continue;
    const list = groups.get(key) ?? [];
    list.push(e);
    groups.set(key, list);
  }

  const out: RecurringSpend[] = [];
  for (const [key, list] of groups) {
    const byDate = [...list].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
    const perDay = byDate.filter((e, i) => i === 0 || e.date !== byDate[i - 1]?.date);
    if (perDay.length < min) continue;

    const intervals = perDay.slice(1).map((e, i) => daysBetween(perDay[i]!.date, e.date));
    const cadence = (Object.keys(WINDOWS) as Cadence[]).find((c) =>
      intervals.every((n) => n >= WINDOWS[c][0] && n <= WINDOWS[c][1]),
    );
    if (!cadence) continue;

    const amounts = perDay.map((e) => e.amountMinor);
    const typical = median(amounts);
    if (!amounts.every((a) => Math.abs(a - typical) <= typical * tolerance)) continue;

    const last = perDay[perDay.length - 1]!;
    out.push({
      key,
      label: (last.note ?? key).trim(),
      cadence,
      typicalAmountMinor: typical,
      occurrences: perDay.length,
      lastDate: last.date,
      nextExpected:
        cadence === 'monthly'
          ? addMonths(last.date, 1)
          : addDays(last.date, cadence === 'weekly' ? 7 : 14),
      entryIds: perDay.map((e) => e.id),
    });
  }
  return out.sort(
    (a, b) => b.typicalAmountMinor - a.typicalAmountMinor || a.key.localeCompare(b.key),
  );
}
